import { defineStore } from 'pinia';
import {
  paginatedFeedOnContainerScroll,
  paginatedFeedRecoverIfStuck,
} from '@/domain/market/paginated-feed-core.js';
import { runPaginatedFeedFetchPage, runPaginatedFeedLoadMore } from '@/stores/feed-fetch-error.js';
import { shouldIgnoreFeedScrollLoad } from '@/composables/feed-viewport.js';
import { createSportFeedFetchOptions } from '@/domain/sport/sport-feed-adapter.js';
import { paginatedFeedIsManualLoadRequired } from '@/domain/market/paginated-feed-pagination.js';

const sportFetchOptions = createSportFeedFetchOptions();

/**
 * Sport live Feed 状态源（paginated-feed-core + sport-feed-adapter）。
 * 视图层保留 SportMatchRow；滚动由 FeedScrollRoot + usePaginatedFeedPage 驱动。
 */
export const useSportFeedStore = defineStore('sportFeed', {
  state: () => ({
    initialized: false,
    sportsSlug: '',
    rawRows: [],
    listLoading: false,
    loadingMore: false,
    hasMore: true,
    pageNum: 0,
    loadGeneration: 0,
    reloadToken: 0,
    lastFetchPlatform: 'pc',
    autoAppendCount: 0,
    appendAutoLoadBlocked: false,
    listFetchError: null,
  }),

  getters: {
    showListEmpty(state) {
      return !state.listLoading && !state.loadingMore && state.rawRows.length === 0;
    },
    manualLoadRequired(state) {
      return paginatedFeedIsManualLoadRequired(state);
    },
  },

  actions: {
    setLastFetchPlatform(isH5) {
      this.lastFetchPlatform = isH5 ? 'h5' : 'pc';
    },

    clear() {
      this.loadGeneration += 1;
      this.sportsSlug = '';
      this.rawRows = [];
      this.hasMore = false;
      this.pageNum = 0;
      this.listLoading = false;
      this.loadingMore = false;
      this.initialized = false;
      this.appendAutoLoadBlocked = false;
      this.listFetchError = null;
    },

    async setSportsSlug(slug) {
      const next = String(slug ?? '').trim();
      if (next === this.sportsSlug && this.initialized) return;
      this.sportsSlug = next;
      this.reloadToken += 1;
      this.initialized = false;
      await this.fetchPage(false);
    },

    recoverIfStuck() {
      paginatedFeedRecoverIfStuck(this);
    },

    async ensureInit(sportsSlug) {
      if (sportsSlug !== undefined && sportsSlug !== null) {
        this.sportsSlug = String(sportsSlug).trim();
      }
      if (this.initialized && this.rawRows.length) return;
      this.recoverIfStuck();
      if (this.initialized && this.rawRows.length) return;
      await this.fetchPage(false);
    },

    async fetchPage(append = false) {
      await runPaginatedFeedFetchPage(this, append, sportFetchOptions);
    },

    async loadMore(options = {}) {
      await runPaginatedFeedLoadMore(this, {
        ...sportFetchOptions,
        ...options,
      });
    },

    onContainerScroll(container, options = {}) {
      paginatedFeedOnContainerScroll(this, container, {
        ...options,
        fetchOptions: sportFetchOptions,
        shouldIgnoreScrollLoad: shouldIgnoreFeedScrollLoad,
      });
    },
  },
});
